import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import type { MapSearchArea } from '@voll-intelligence/intelligence-ui-kit';
import {
  GenUiConfigProvider,
  useGenUiConfig,
  type GenUiConfig,
} from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import {
  HOTEL_DOCK_OFFER_LIMIT,
  type ChatUiHotelOffer,
  type HotelPanelState,
  type HotelPlannerBlock,
  type HotelPlannerSelection,
  type HotelPlannerPort,
  type HotelRoomsRateFilters,
} from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { HotelDetailsDrawer } from './hotel_details_drawer.js';
import { HotelResultsOrganism } from './hotel_results_organism.js';
import { blockSelection, deriveStage, drawerStay, mapAreaSelection } from './hotel_selection.js';
import { createHotelReadCache, disposeHotelReadCache } from './hotel_read_cache.js';
// O cache da gaveta também é o da lista: ela tira o preço do hotel sem quarto (#4469).
import { HotelReadCacheContext } from './hotel_sold_out.js';
import { tapRefineContext } from './tap_refine.js';
import { useHotelSearchContextSync } from './use_hotel_search_context_sync.js';
import { useHotelSearchPreview, type HotelSearchPreviewState } from './use_hotel_search_preview.js';
import type { PanelReport } from './use_panel_report.js';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';

type HotelPlannerCardProps = {
  ui: HotelPlannerBlock;
  /* Sem `note`: a fala do turno é prosa do HOST, acima do card (o organismo tem
     chrome próprio e nenhum slot de rodapé onde ela caberia). */
  readOnly?: boolean;
  /** A conversa andou depois deste card: o refino in-place morre e a busca passa
   *  a sair como turno (mensagem nova + planner novo). */
  superseded?: boolean;
  busy?: boolean;
  /** Bloco remontado do histórico, não da emissão ao vivo (ver
   *  `GenUiRenderProps.replay`): o gesto `directives.open` já foi gasto. */
  replay?: boolean;
  dispatch: GenUiDispatch;
};

/** A prévia de um bloco cuja busca não é a da vez: uma referência só, para os
 *  memos que dependem dela não recalcularem a cada render. */
const IDLE_PREVIEW: HotelSearchPreviewState = { phase: 'idle' };

/** Onde a gaveta do card monta: dentro da lista ou do mapa cheio quando um
 *  deles está aberto (o organismo a aninha), senão no próprio card. */
function placeDetailsLayer(
  layer: ReactNode,
  screen: PanelReport,
): { nested: ReactNode; standalone: ReactNode } {
  return screen.panel?.open === true
    ? { nested: layer, standalone: null }
    : { nested: undefined, standalone: layer };
}

function focusDetailsSuccessor(root: HTMLElement | null, offerId: string): void {
  const fullscreen = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-hotel-map-fullscreen-dialog="true"], [data-hotel-all-results-dialog="true"]',
    ),
  ).at(-1);
  if (fullscreen) {
    const marker = Array.from(fullscreen.querySelectorAll<HTMLElement>('[data-hotel-id]')).find(
      (candidate) => candidate.dataset.hotelId === offerId,
    );
    (marker ?? fullscreen).focus();
    return;
  }
  const successor = Array.from(
    root?.querySelectorAll<HTMLElement>('[data-hotel-details-trigger]') ?? [],
  ).find((candidate) => candidate.dataset.hotelDetailsTrigger === offerId);
  successor?.focus();
}

/** Fechada a gaveta, o foco volta a quem a abriu — ou, se ele saiu de cena, ao
 *  sucessor da oferta (`focusDetailsSuccessor`). Depois do commit que a fechou. */
function returnDetailsFocus(
  openerRef: RefObject<HTMLElement | null>,
  hostRef: RefObject<HTMLElement | null>,
  offerId: string | null,
): void {
  const opener = openerRef.current;
  queueMicrotask(() => {
    if (opener?.isConnected) {
      opener.focus();
    } else if (offerId) {
      focusDetailsSuccessor(hostRef.current, offerId);
    }
  });
}

/** O rótulo do destino segue a busca que ANCOROU a cena (#3617): um `place` do
 *  bloco sobrevivendo a uma busca local noutro destino titulava hotéis de São
 *  Paulo com o lugar que o agente tinha resolvido antes (Arlington, VA). A
 *  âncora do bloco cai junto quando o destino novo não trouxe coordenada — a
 *  resposta traz a dela. Sem destino na seleção (só datas ou filtros), o do
 *  bloco segue valendo. */
function withSelectionPlace(
  ui: HotelPlannerBlock,
  selection: HotelPlannerSelection | undefined,
): HotelPlannerBlock {
  if (!selection?.place && !selection?.placeDetails && !selection?.placeRef) return ui;
  const next = { ...ui };
  delete next.place;
  if (!selection.placeDetails) delete next.placeDetails;
  return selection.place ? { ...next, place: selection.place } : next;
}

export function resolveHotelPlannerPreviewUi(
  ui: HotelPlannerBlock,
  previewState: HotelSearchPreviewState,
): HotelPlannerBlock {
  // Busca EM VOO: o destino da vez já é o da seleção, mesmo sem resposta — e
  // vence até o da busca ANTERIOR, que continua em `data` enquanto esta corre.
  // Sem isto a barra anunciava a cidade nova e o mapa seguia ancorado na velha,
  // que é justamente o que precisa mudar de lugar. As ofertas ficam como estão:
  // quem some da superfície é o `stage` de carregando (o organismo esvazia dock
  // e mapa), e zerá-las aqui fecharia a gaveta aberta.
  const pendingSelection =
    previewState.phase === 'loading' ? previewState.pendingSelection : undefined;
  const pending = pendingSelection?.placeDetails;
  const placed = withSelectionPlace(ui, pendingSelection ?? previewState.selection);
  if (!previewState.data) return pending ? { ...placed, placeDetails: pending } : placed;
  const next = {
    ...placed,
    offers: previewState.data.offers,
    // A SESSÃO da busca local também entra. As ofertas em cena são dela, e é
    // nela que a lista completa tem de ser paginada: sem isto o painel seguia
    // pedindo página à sessão do bloco — a que acabou de ser substituída —, e
    // com a busca VENCIDA isso deixava o "Buscar de novo" sem efeito visível
    // (busca nova respondia 200, o painel continuava com o aviso de expirada e
    // as ofertas antigas, porque a sessão morta nunca saía de cena).
    ...(previewState.data.sessionId ? { sessionId: previewState.data.sessionId } : {}),
    placeDetails:
      pending ??
      previewState.data.placeDetails ??
      previewState.selection?.placeDetails ??
      placed.placeDetails,
    nights: previewState.data.nights,
  };
  if (!next.placeDetails) delete next.placeDetails;
  delete next.searchError;
  // As diretivas do AGENTE (M1 · 1.2) morrem com a busca do viajante: elas
  // apontam ordinais e filtram a lista ANTERIOR, e mantê-las sobre resultados
  // novos quebra "cena = card" — o card filtraria/ordenaria/destacaria linhas
  // que o agente nunca viu (o contexto de refino sobe as ofertas CRUAS, e é
  // por elas que o próximo ordinal conta). Quem dirige o card agora é quem
  // acabou de buscar nele.
  delete next.directives;
  return next;
}

/** `directives.open` é GESTO do turno, não estado da cena: abre a superfície
 *  uma vez, quando o bloco é emitido ao vivo — o organismo lê a diretiva no
 *  mount (`useHotelResultsDialogs`), e o card remonta por emissão. O bloco
 *  PERSISTIDO carrega a mesma diretiva, e o `/history` remonta a thread inteira
 *  a cada reload/reabertura: honrá-la de novo cobria a conversa com o mapa
 *  cheio ao reabri-la (#3396). No replay o gesto sai do bloco efetivo; filtro,
 *  ordem e destaque ficam — eles SÃO a cena que o agente montou. */
function spendOpenGesture(ui: HotelPlannerBlock, replay: boolean | undefined): HotelPlannerBlock {
  if (!replay || ui.directives?.open === undefined) return ui;
  const directives = { ...ui.directives };
  delete directives.open;
  return { ...ui, directives };
}

/** Thin wrapper over the search+results organism: derives the stage from the
 *  block and re-seeds the whole surface when the challenge changes.
 *
 *  O drawer de detalhes vive AQUI, fora do `key={challengeId}`: uma seleção
 *  dispara o turno do agente, que reemite o card com challengeId novo e REMONTA
 *  o organismo — se o drawer morasse lá, fecharia sozinho no meio da leitura.
 *  Portal no body porque a superfície do organismo é um contexto posicionado. */
/** A oferta que a gaveta lê, e de onde ela sai.
 *
 *  O bloco é a primeira fonte: refinamento local troca a lista e a gaveta
 *  acompanha a versão nova, e a oferta sair do bloco fecha a gaveta (ver o
 *  efeito de fechamento). A linha PAGINADA não vive no bloco — a rota de página
 *  emite handles próprios —, então para ela a oferta guardada é a fonte; sem
 *  isso, toda linha além das 6 do bloco era clique morto. `rateFilters` é o
 *  recorte de tarifa da lista no clique (#5820): os quartos abrem com ele. */
type DetailsTarget = {
  offer: ChatUiHotelOffer;
  paged: boolean;
  rateFilters: HotelRoomsRateFilters;
};

function detailsTargetOf(
  offer: ChatUiHotelOffer,
  blockOffers: readonly ChatUiHotelOffer[] | undefined,
  rateFilters: HotelRoomsRateFilters,
): DetailsTarget {
  const paged = !blockOffers?.some((candidate) => candidate.offerId === offer.offerId);
  return { offer, paged, rateFilters };
}

function resolveDetailsTarget(
  target: DetailsTarget | null,
  blockOffers: readonly ChatUiHotelOffer[] | undefined,
): DetailsTarget | null {
  if (!target) return null;
  const inBlock = blockOffers?.find((candidate) => candidate.offerId === target.offer.offerId);
  if (inBlock) return { ...target, offer: inBlock };
  return target.paged ? target : null;
}

/** A gaveta de detalhes do card: a oferta guardada, a gaveta em cena (memoizada,
 *  resolvida contra o bloco), o fechar com a volta do foco e as âncoras desse foco
 *  (também a do gatilho da lista cheia, `restoreExpandedFocus`). */
function useDetailsDrawer(blockOffers: readonly ChatUiHotelOffer[] | undefined) {
  const [detailsTarget, setDetailsTarget] = useState<DetailsTarget | null>(null);
  const detailsOfferId = detailsTarget?.offer.offerId ?? null;
  const resultsHostRef = useRef<HTMLDivElement>(null);
  const detailsOpenerRef = useRef<HTMLElement | null>(null);
  const openDetails = useMemo(
    () => resolveDetailsTarget(detailsTarget, blockOffers),
    [detailsTarget, blockOffers],
  );
  const closeDetails = useCallback(() => {
    setDetailsTarget(null);
    returnDetailsFocus(detailsOpenerRef, resultsHostRef, detailsOfferId);
  }, [detailsOfferId]);
  // A oferta sumir do bloco fecha o painel por derivação; esquecer o id (no render)
  // é o que impede uma reemissão POSTERIOR que a traga de volta de reabri-lo sozinha.
  // O foco volta depois do commit.
  const [vanished, setVanished] = useState<{ offerId: string } | null>(null);
  if (detailsOfferId !== null && openDetails === null) {
    setDetailsTarget(null);
    setVanished({ offerId: detailsOfferId });
  }
  useEffect(() => {
    if (vanished) returnDetailsFocus(detailsOpenerRef, resultsHostRef, vanished.offerId);
  }, [vanished]);
  const restoreExpandedFocus = useCallback(() => {
    queueMicrotask(() => {
      resultsHostRef.current
        ?.querySelector<HTMLElement>('[data-hotel-results-expand-trigger]')
        ?.focus();
    });
  }, []);
  return {
    detailsOfferId,
    setDetailsTarget,
    openDetails,
    closeDetails,
    restoreExpandedFocus,
    resultsHostRef,
    detailsOpenerRef,
  };
}

/**
 * "Buscar nesta área" (#3114) no cartão da CONVERSA (#3618): aqui o host não
 * injeta `hotelSearchArea` (é port da bancada, que congela um pedido novo) — a
 * área visível vira uma busca do próprio cartão pelo port de leitura, com a
 * estadia da busca em cena. Só no cartão ativo: superado ou só-leitura, a busca
 * já é turno, e o mapa segue sem a oferta.
 */
function useCardSearchAreaConfig({
  config,
  searched,
  block,
  readOnly,
  superseded,
  search,
  port,
}: {
  readonly config: GenUiConfig;
  /** A seleção da última busca do cartão, senão a do bloco: a estadia em cena. */
  readonly searched: HotelPlannerSelection | undefined;
  readonly block: HotelPlannerSelection | undefined;
  readonly readOnly: boolean;
  readonly superseded: boolean;
  readonly search: (selection: HotelPlannerSelection) => Promise<void>;
  /** O port de leitura do host: sem `searchHotels` a busca do cartão só cai no
   *  erro — melhor não oferecer o botão. */
  readonly port: HotelPlannerPort | undefined;
}): GenUiConfig {
  const { t } = useGenUiI18n();
  const base = searched ?? block;
  const canSearch = typeof port?.searchHotels === 'function';
  return useMemo(() => {
    if (config.hotelSearchArea || readOnly || superseded || !canSearch || !base) return config;
    const label = t('hotel.results.mapArea');
    return {
      ...config,
      hotelSearchArea: (area: MapSearchArea) => search(mapAreaSelection(base, area, label)),
    };
  }, [base, canSearch, config, readOnly, search, superseded, t]);
}

export function HotelPlannerCard({
  ui,
  readOnly = false,
  superseded = false,
  busy = false,
  replay,
  dispatch,
}: Readonly<HotelPlannerCardProps>): ReactNode {
  const [cache] = useState(createHotelReadCache);
  useEffect(
    () => () => {
      disposeHotelReadCache(cache);
    },
    [cache],
  );
  const config = useGenUiConfig();
  const port = config.ports?.hotelPlanner;
  const preview = useHotelSearchPreview({
    port,
    cache,
    challengeId: ui.challengeId,
    ...(ui.sessionId ? { sessionId: ui.sessionId } : {}),
  });
  const previewState = preview.state.challengeId === ui.challengeId ? preview.state : IDLE_PREVIEW;
  const effectiveUi = useMemo(
    () => spendOpenGesture(resolveHotelPlannerPreviewUi(ui, previewState), replay),
    [previewState, replay, ui],
  );
  // Guarda a oferta, não só o ID: refinamentos locais podem substituir a lista e
  // a gaveta acompanha a versão nova (ver `resolveDetailsTarget`), mas a linha
  // PAGINADA não vive no bloco — para ela a oferta guardada é a fonte.
  const {
    detailsOfferId,
    setDetailsTarget,
    openDetails,
    closeDetails,
    restoreExpandedFocus,
    resultsHostRef,
    detailsOpenerRef,
  } = useDetailsDrawer(effectiveUi.offers);
  // M1 · 1.1b: o painel que o ORGANISMO reporta (filtros, ordem, visíveis).
  const [organismScreen, setOrganismScreen] = useState<PanelReport>({});
  // A gaveta de detalhes é estado do CARD, não do organismo — entra por cima do
  // que o organismo reportou, para o painel refletir "hotel aberto" mesmo
  // quando quem abriu foi o dock (fora do painel "Ver todos os hotéis").
  const screen = useMemo<PanelReport>(() => {
    if (detailsOfferId === null) return organismScreen;
    const panel: HotelPanelState = {
      ...(organismScreen.panel ?? { open: false }),
      openOfferId: detailsOfferId,
    };
    return { ...organismScreen, panel };
  }, [detailsOfferId, organismScreen]);
  // #2477: a busca client-side do card não passa pelo FSM. Ao concluir, avisa o
  // host (que guarda os critérios + sessão + o painel para anexar ao próximo
  // refino de texto). Card só-leitura ou superado não sincroniza — ali a busca
  // já vira turno, que leva o contexto ao agente pelo caminho normal.
  // #2857: o card EMITIDO PELO AGENTE (sem busca client-side) reporta o painel
  // com a seleção do BLOCO — destino + estadia como o card os mostra.
  const blockSel = useMemo(() => blockSelection(ui), [ui]);
  useHotelSearchContextSync({
    dispatch,
    challengeId: ui.challengeId,
    blockId: ui.id,
    state: previewState,
    blockSessionId: ui.sessionId,
    readOnly,
    superseded,
    screen,
    blockSelection: blockSel,
  });
  const areaConfig = useCardSearchAreaConfig({
    config,
    searched: previewState.selection,
    block: blockSel,
    readOnly,
    superseded,
    search: preview.search,
    port,
  });
  // Gate on the method being callable, not just the port object's presence —
  // a host supplying a partial `hotelPlanner` port (e.g. only `searchHotelPlaces`)
  // must still degrade fail-soft instead of throwing inside `useHotelDetails`.
  const detailsPort = typeof port?.getHotelDetails === 'function';
  // A estadia do bloco desce pro drawer: o card de quartos precisa das noites pra
  // dividir o total em diária. Memoizada pro `buildHotelRoomsBlock` de lá não
  // reprojetar a cada render.
  const stay = useMemo(() => drawerStay(ui, previewState.selection), [previewState.selection, ui]);
  // A escolha do QUARTO encerra a leitura: ela vira turno e o próximo card cai
  // na thread — atrás do painel "Ver todos os hotéis" (e do mapa cheio), que
  // vivem DENTRO do organismo e não fecham com a gaveta. Remontar o organismo é
  // o mesmo caminho que uma reemissão de `challengeId` já usa, `onExpandedRemount`
  // incluído (o foco volta ao gatilho da lista). Só o caminho CONVERSACIONAL
  // passa por aqui: a bancada determinística monta a própria página
  // (`HotelDeterministicResultsPage`) e o detalhe, onde fechar é do host.
  const [surfaceEpoch, setSurfaceEpoch] = useState(0);
  const dispatchFromDrawer = useCallback<GenUiDispatch>(
    (action) => {
      if (action.type === 'hotel-room-select') {
        setSurfaceEpoch((epoch) => epoch + 1);
        // M1 · 1.1a: a escolha do quarto é o último toque antes do checkout — se
        // a lista saiu da busca client-side, é aqui que a cena chega ao agente.
        // M1 · 1.1b: o painel/total em cena viajam junto.
        const refine = tapRefineContext(effectiveUi, previewState, screen);
        dispatch(refine ? { ...action, refine } : action);
        return;
      }
      dispatch(action);
    },
    [dispatch, effectiveUi, previewState, screen],
  );
  // A gaveta é do card. Por cima da lista ou do mapa cheio ela monta DENTRO
  // deles (diálogo aninhado, pelo organismo); sem eles, aqui, fora da chave
  // do organismo — uma reemissão do bloco não a remonta.
  const detailsLayer = openDetails && detailsPort && (
    <HotelDetailsDrawer
      offer={openDetails.offer}
      stay={stay}
      rateFilters={openDetails.rateFilters}
      {...(effectiveUi.placeDetails ? { anchor: effectiveUi.placeDetails } : {})}
      /* Só o `readOnly` do host: `ui.viewOnly` trava a BUSCA lá fora e é
         justamente nesse estado que a escolha do QUARTO acontece aqui
         dentro. Conversa encerrada (ou planner/checkout mais novo em
         cena) continua congelando as duas. */
      readOnly={readOnly}
      busy={busy}
      cache={cache}
      dispatch={dispatchFromDrawer}
      onClose={closeDetails}
    />
  );
  const details = placeDetailsLayer(detailsLayer, organismScreen);
  return (
    <HotelReadCacheContext.Provider value={cache}>
      {/* O mapa do card veste a elevação do bloco da thread (#6296). */}
      <div ref={resultsHostRef} className={`contents ${BLOCK_ELEVATION}`}>
        <GenUiConfigProvider config={areaConfig}>
          <HotelResultsOrganism
            key={`${ui.challengeId}#${String(surfaceEpoch)}`}
            ui={effectiveUi}
            /* Toda busca em voo é carregando — inclusive o refino que já tem lista
             em cena. Enquanto o gate era "só sem ofertas", trocar o filtro
             deixava a lista e o mapa ANTIGOS no ar sob critérios novos, sem
             nenhum sinal de que havia busca correndo. */
            stage={deriveStage(effectiveUi, busy || previewState.phase === 'loading')}
            readOnly={readOnly}
            superseded={superseded}
            busy={busy}
            replay={replay}
            dispatch={dispatch}
            previewState={previewState}
            /* Paridade com a bancada determinística: o card abre COMPACTO com o teto
             do kit de hotéis no dock/pinos (o servidor já ranqueia
             "já-ficou-primeiro"); o resto fica atrás de "Ver todos os hotéis". */
            dockOfferLimit={HOTEL_DOCK_OFFER_LIMIT}
            /* Card semeado (#2152) espera o gesto: `autoSearch: false` no bloco
             suprime o disparo automático; ausente = comportamento histórico. */
            autoSearch={effectiveUi.autoSearch ?? true}
            onSearch={preview.search}
            onRetry={preview.retry}
            onOpenDetails={(offer, rateFilters) => {
              detailsOpenerRef.current =
                document.activeElement instanceof HTMLElement ? document.activeElement : null;
              setDetailsTarget(detailsTargetOf(offer, effectiveUi.offers, rateFilters));
            }}
            detailsLayer={detailsLayer}
            onExpandedRemount={restoreExpandedFocus}
            onPanelReport={setOrganismScreen}
          />
        </GenUiConfigProvider>
      </div>
      {details.standalone}
    </HotelReadCacheContext.Provider>
  );
}
